import React, {
  useState, useEffect, Suspense, lazy,
  memo, useMemo, useCallback, useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  Box, Container, IconButton, Collapse,
  Paper, Skeleton, Typography, Fade, Zoom,
  Tooltip, Badge, Chip, alpha,
} from "@mui/material";
import { keyframes } from "@mui/system";
import {
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Widgets as WidgetsIcon,
} from "@mui/icons-material";
import { widgetsApi } from "../../api/widget-api";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { debounce } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Cache module-level
// ---------------------------------------------------------------------------
const _widgetCache = new Map();

// ---------------------------------------------------------------------------
// Constantes de dimensionnement
// ---------------------------------------------------------------------------
const ROW_HEIGHT_PX = 90;

const WIDGET_ROW_HEIGHTS = {
  5: 7.1, 12: 5.5, 13: 7.1, 10: 5.4, 4: 5.1,
  11: 5.3, 9: 5.5, 8: 5.1, 6: 5.1, 7: 5.1,
};
const WIDGET_WIDTHS = {
  5: 19, 12: 7.5, 13: 19, 10: 19, 4: 7.3,
  11: 7.5, 9: 7.5, 8: 7.3, 6: 7.3, 7: 19,
};

// Fallback par nom de composant (si widget_id non référencé)
const COMPONENT_ROW_HEIGHTS = {
  "satisfaction-details.js":              5.5,
  "satisfaction-questionnaire-dates.js":  7.1,
  "best-satisfaction-rates.js":           7.1,
};
const COMPONENT_WIDTHS = {
  "satisfaction-details.js":              7.5,
  "satisfaction-questionnaire-dates.js":  19,
  "best-satisfaction-rates.js":           19,
};

const getWidgetRowHeight = (id, componentName) =>
  WIDGET_ROW_HEIGHTS[id] ?? COMPONENT_ROW_HEIGHTS[componentName] ?? 1.7;
const getWidgetWidth = (id, componentName) =>
  WIDGET_WIDTHS[id] ?? COMPONENT_WIDTHS[componentName] ?? 4.7;

// ---------------------------------------------------------------------------
// Widgets par défaut
// ---------------------------------------------------------------------------
const defaultWidgets = [
  { widget_id: "5",  component_name: "mean-audit.js",                       position_x: 0,    position_y: 7.1,   width: 14.0, height: 7.1 },
  { widget_id: "3",  component_name: "satisfaction-rate.js",                position_x: 10.0, position_y: 5.4,   width: 4.0,  height: 1.7 },
  { widget_id: "1",  component_name: "finished-epp.js",                     position_x: 5.0,  position_y: 5.4,   width: 4.0,  height: 1.7 },
  { widget_id: "2",  component_name: "in-progress-epp.js",                  position_x: 0.0,  position_y: 5.4,   width: 4.0,  height: 1.7 },
  { widget_id: "4",  component_name: "last-activities.js",                  position_x: 0.0,  position_y: 0.0,   width: 6.6,  height: 5.4 },
  { widget_id: "8",  component_name: "best-finished-epp.js",                position_x: 7.3,  position_y: 0.0,   width: 6.7,  height: 5.4 },
  { widget_id: "11", component_name: "last-messages.js",                    position_x: 7.5,  position_y: 5.2,   width: 7.5,  height: 5.1 },
  { widget_id: "10", component_name: "coming-dates.js",                     position_x: 0,    position_y: 0,     width: 15.0, height: 5.2 },
  { widget_id: "9",  component_name: "feedbacks-number.js",                 position_x: 0,    position_y: 5.2,   width: 7.5,  height: 5.1 },
  { widget_id: "12", component_name: "satisfaction-details.js",             position_x: 0,    position_y: 14.2,  width: 7.5,  height: 5.5 },
  { widget_id: "7",  component_name: "best-satisfaction-rates.js",          position_x: 0,    position_y: 21.3,  width: 19,   height: 7.1 },
  { widget_id: "13", component_name: "satisfaction-questionnaire-dates.js", position_x: 0,    position_y: 28.4,  width: 19,   height: 7.1 },
];

const defaultLayoutItems = defaultWidgets.map((w) => ({
  i: w.widget_id.toString(),
  x: w.position_x,
  y: w.position_y,
  w: w.width,
  h: w.height,
  component_name: w.component_name,
}));

// ---------------------------------------------------------------------------
// Chargement des widgets
// ---------------------------------------------------------------------------
const fetchAllWidgets = async () => {
  const widgets = await widgetsApi.getWidgets();
  return widgets.map((widget) => ({
    ...widget,
    Component: lazy(() =>
      import(`./widgets/${widget.component_name}`).catch(() =>
        import(`../../components/dashboard/statistiques/${widget.component_name}`)
      )
    ),
  }));
};

const fetchWidgetLocations = async (admin_id) => {
  try {
    const res  = await widgetsApi.getWidgetLocations(admin_id);
    const data = res.data;

    if (!data || data.length === 0) {
      return { locations: defaultLayoutItems, isDefault: true };
    }

    return {
      locations: data.map((loc) => ({
        i: loc.widget_id.toString(),
        x: loc.position_x,
        y: loc.position_y,
        w: loc.width  || 4,
        h: loc.height || 4,
      })),
      isDefault: false,
    };
  } catch {
    return { locations: defaultLayoutItems, isDefault: true };
  }
};

// ---------------------------------------------------------------------------
// Skeleton amélioré avec animation pulse
// ---------------------------------------------------------------------------
const WidgetFallback = memo(({ widgetId, componentName }) => {
  const height = Math.round(getWidgetRowHeight(widgetId, componentName) * ROW_HEIGHT_PX);
  return (
    <Skeleton
      variant="rounded"
      width="100%"
      height={height}
      animation="pulse"
      sx={{
        borderRadius: 3,
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
      }}
    />
  );
});

// ---------------------------------------------------------------------------
// Widget Item avec effets visuels améliorés
// ---------------------------------------------------------------------------
const WidgetItem = memo(({ widget, isEditMode, onDelete, index = 0 }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      position: "relative",
      animation: `${fadeInUp} 0.45s ease both`,
      animationDelay: `${index * 60}ms`,
      "&:hover .delete-btn": {
        opacity: 1,
        transform: "scale(1)",
      },
    }}
  >
    {isEditMode && (
      <Zoom in={true}>
        <IconButton
          className="delete-btn"
          onClick={(e) => { e.stopPropagation(); onDelete(widget.id); }}
          sx={{
            position: "absolute",
            top: -10,
            right: -10,
            zIndex: 100,
            backgroundColor: "#ff4757",
            color: "#fff",
            width: 28,
            height: 28,
            opacity: 0,
            transform: "scale(0.8)",
            transition: "all 0.2s ease",
            boxShadow: "0 4px 12px rgba(255, 71, 87, 0.4)",
            "&:hover": {
              backgroundColor: "#ff3838",
              transform: "scale(1.1)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Zoom>
    )}

    <Suspense fallback={<WidgetFallback widgetId={widget.id} componentName={widget.component_name} />}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          },
        }}
      >
        <widget.Component />
      </Box>
    </Suspense>
  </Box>
));

// ---------------------------------------------------------------------------
// Composant principal amélioré
// ---------------------------------------------------------------------------
const DashboardAdmin = ({ admin_id, isEditMode, id_service }) => {
  const cacheKey = `${admin_id}_${id_service}`;
  const cached   = _widgetCache.get(cacheKey);

  const [availableWidgets, setAvailableWidgets] = useState(cached?.availableWidgets ?? []);
  const [widgetsLayout,    setWidgetsLayout]    = useState(cached?.widgetsLayout    ?? []);
  const [selectedWidgets,  setSelectedWidgets]  = useState(cached?.selectedWidgets  ?? []);
  const [menuOpen,         setMenuOpen]         = useState(true);
  const [hoveredMenuItem,  setHoveredMenuItem]  = useState(null);
  const [isLoading,        setIsLoading]        = useState(!cached);

  const saveRef = useRef(null);

  // -------------------------------------------------------------------------
  // Sauvegarde en DB
  // -------------------------------------------------------------------------
  const handleSaveWidgets = useCallback(async (updatedLayout, widgetsList = null) => {
    const list = widgetsList ?? selectedWidgets;
    const dataToSend = updatedLayout.map((layout) => {
      const widget = list.find((w) => w.id.toString() === layout.i);
      return {
        widget_id:    layout.i,
        component_name: widget ? widget.component_name : "unknown",
        position_x:  layout.x,
        position_y:  layout.y,
        width:        layout.w,
        height:       layout.h,
      };
    });
    try {
      await widgetsApi.saveWidgetLocations(admin_id, dataToSend);
    } catch { /* silencieux */ }
  }, [admin_id, selectedWidgets]);

  useEffect(() => { saveRef.current = handleSaveWidgets; }, [handleSaveWidgets]);

  // -------------------------------------------------------------------------
  // Synchronisation cache
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (availableWidgets.length > 0) {
      _widgetCache.set(cacheKey, { availableWidgets, widgetsLayout, selectedWidgets });
    }
  }, [availableWidgets, widgetsLayout, selectedWidgets, cacheKey]);

  // -------------------------------------------------------------------------
  // Initialisation
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (_widgetCache.has(cacheKey)) return;

    setIsLoading(true);
    const SERVICE_WIDGETS = ["last-messages.js", "coming-dates.js", "feedbacks-number.js", "last-activities-ex.js"];

    const init = async () => {
      const [allWidgets, { locations, isDefault }] = await Promise.all([
        fetchAllWidgets(),
        fetchWidgetLocations(admin_id),
      ]);

      const filtered = id_service !== 0
        ? allWidgets.filter((w) =>  SERVICE_WIDGETS.includes(w.component_name))
        : allWidgets.filter((w) => !SERVICE_WIDGETS.includes(w.component_name));

      const selectedIds = locations.map((l) => l.i);
      const selected    = filtered.filter((w) => selectedIds.includes(w.id.toString()));

      setAvailableWidgets(filtered);
      setSelectedWidgets(selected);
      setWidgetsLayout(locations);
      setIsLoading(false);

      if (isDefault && selected.length > 0) {
        widgetsApi.saveWidgetLocations(
          admin_id,
          defaultWidgets.map((dw) => ({
            widget_id:      dw.widget_id,
            component_name: dw.component_name,
            position_x:     dw.position_x,
            position_y:     dw.position_y,
            width:          dw.width,
            height:         dw.height,
          }))
        ).catch(() => {});
      }
    };

    init().catch(() => setIsLoading(false));
  }, [admin_id, id_service, cacheKey]);

  // -------------------------------------------------------------------------
  // handleLayoutChange
  // -------------------------------------------------------------------------
  const handleLayoutChange = useMemo(
    () =>
      debounce((newLayout) => {
        setWidgetsLayout(newLayout);
        saveRef.current?.(newLayout);
      }, 300),
    []
  );

  useEffect(() => () => handleLayoutChange.cancel(), [handleLayoutChange]);

  // -------------------------------------------------------------------------
  // Ajout / suppression de widgets
  // -------------------------------------------------------------------------
  const onSelectWidget = useCallback((widget) => {
    if (selectedWidgets.some((w) => w.id === widget.id)) return;

    const maxY = Math.max(...widgetsLayout.map((l) => l.y + l.h), 0);
    const newItem = {
      i: widget.id.toString(),
      x: 0,
      y: maxY,
      w: getWidgetWidth(widget.id, widget.component_name),
      h: getWidgetRowHeight(widget.id, widget.component_name)
    };
    const updated = [...widgetsLayout, newItem];

    setWidgetsLayout(updated);
    setSelectedWidgets((prev) => {
      const next = [...prev, widget];
      saveRef.current?.(updated, next);
      return next;
    });
  }, [selectedWidgets, widgetsLayout]);

  const handleDeleteWidget = useCallback((widgetId) => {
    const updatedLayout   = widgetsLayout.filter((l) => l.i !== widgetId.toString());
    const updatedSelected = selectedWidgets.filter((w) => w.id !== widgetId);

    setWidgetsLayout(updatedLayout);
    setSelectedWidgets(updatedSelected);
    saveRef.current?.(updatedLayout, updatedSelected);
  }, [widgetsLayout, selectedWidgets]);

  // -------------------------------------------------------------------------
  // Rendu mémoïsé de la grille
  // -------------------------------------------------------------------------
  const gridItems = useMemo(() =>
    widgetsLayout.map((layout, index) => {
      const widget = availableWidgets.find((w) => w.id.toString() === layout.i);
      if (!widget) return null;

      const widgetLayout = {
        ...layout,
        h: getWidgetRowHeight(widget.id, widget.component_name),
        w: getWidgetWidth(widget.id, widget.component_name),
      };

      return (
        <div key={layout.i} data-grid={widgetLayout}>
          <WidgetItem
            widget={widget}
            isEditMode={isEditMode}
            onDelete={handleDeleteWidget}
            index={index}
          />
        </div>
      );
    }),
  [widgetsLayout, availableWidgets, isEditMode, handleDeleteWidget]);

  // -------------------------------------------------------------------------
  // Écran de chargement amélioré
  // -------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Box sx={{
        p: 4,
        width: "100%",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
      }}>
        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width="32%"
              height={160}
              animation="wave"
              sx={{ borderRadius: 3, opacity: 0.6 }}
            />
          ))}
        </Box>
        <Skeleton variant="rounded" width="100%" height={450} animation="wave" sx={{ borderRadius: 3, mb: 3 }} />
        <Box sx={{ display: "flex", gap: 3 }}>
          <Skeleton variant="rounded" width="50%" height={320} animation="wave" sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" width="50%" height={320} animation="wave" sx={{ borderRadius: 3 }} />
        </Box>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Rendu principal amélioré
  // -------------------------------------------------------------------------
  // Sidebar rendue via Portal — hors du DOM zoomé pour éviter le piège CSS transform
  const sidebarPortal = isEditMode && ReactDOM.createPortal(
    <Fade in={isEditMode}>
      <Box
        sx={{
          position: "fixed",
          right: 0,
          top: 64,
          height: "calc(100vh - 64px)",
          width: menuOpen ? 280 : 70,
          background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
          borderLeft: "1px solid rgba(0,0,0,0.06)",
          boxShadow: menuOpen ? "-4px 0 20px rgba(0,0,0,0.08)" : "none",
          transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1400,
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
            {/* Bouton toggle amélioré */}
            <Tooltip title={menuOpen ? "Réduire" : "Étendre"} placement="left">
              <IconButton
                onClick={() => setMenuOpen((o) => !o)}
                sx={{
                  position: "absolute",
                  top: 20,
                  left: menuOpen ? 240 : 50,
                  zIndex: 100,
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  width: 40,
                  height: 40,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#6060B8",
                    color: "#fff",
                    transform: "scale(1.1)",
                  },
                }}
              >
                {menuOpen ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Tooltip>

            {/* Header du menu */}
            <Collapse in={menuOpen}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <WidgetsIcon sx={{ color: "#6060B8", fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#2d3748",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Widgets
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#718096",
                    display: "block",
                    fontSize: "0.8rem",
                  }}
                >
                  {selectedWidgets.length} actif{selectedWidgets.length > 1 ? 's' : ''} / {availableWidgets.length} disponible{availableWidgets.length > 1 ? 's' : ''}
                </Typography>
              </Box>
            </Collapse>

            {/* Liste des widgets */}
            <Collapse in={menuOpen}>
              <Box sx={{ px: 2, pb: 2, overflowY: "auto", maxHeight: "calc(100vh - 120px)" }}>
                {availableWidgets.map((widget, index) => {
                  const isAlreadySelected = selectedWidgets.some((w) => w.id === widget.id);
                  return (
                    <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={widget.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          padding: 2.5,
                          margin: 1.5,
                          cursor: isAlreadySelected ? "default" : "pointer",
                          backgroundColor: isAlreadySelected ? alpha("#6060B8", 0.08) : "#fff",
                          border: "2px solid",
                          borderColor: isAlreadySelected
                            ? alpha("#6060B8", 0.3)
                            : hoveredMenuItem === widget.id
                              ? "#6060B8"
                              : "transparent",
                          borderRadius: 3,
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: hoveredMenuItem === widget.id && !isAlreadySelected ? "translateX(8px)" : "translateX(0)",
                          boxShadow: hoveredMenuItem === widget.id
                            ? "0 8px 25px rgba(96, 96, 184, 0.15)"
                            : "0 2px 8px rgba(0,0,0,0.04)",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: 4,
                            height: "100%",
                            backgroundColor: isAlreadySelected ? "#6060B8" : "transparent",
                            transition: "all 0.3s ease",
                          },
                          "&::after": isAlreadySelected ? {
                            content: '"✓"',
                            position: "absolute",
                            top: 12,
                            right: 12,
                            fontSize: "0.75rem",
                            color: "#6060B8",
                            fontWeight: 700,
                          } : {},
                        }}
                        onClick={() => !isAlreadySelected && onSelectWidget(widget)}
                        onMouseEnter={() => setHoveredMenuItem(widget.id)}
                        onMouseLeave={() => setHoveredMenuItem(null)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              background: isAlreadySelected
                                ? "linear-gradient(135deg, #6060B8 0%, #8282CA 100%)"
                                : "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <DashboardIcon
                              sx={{
                                fontSize: 20,
                                color: isAlreadySelected ? "#fff" : "#6060B8"
                              }}
                            />
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isAlreadySelected ? 600 : 500,
                                color: isAlreadySelected ? "#6060B8" : "#2d3748",
                                fontSize: "0.9rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {widget.name}
                            </Typography>
                            {isAlreadySelected && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#718096",
                                  fontSize: "0.75rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mt: 0.3,
                                }}
                              >
                                <DragIndicatorIcon sx={{ fontSize: 12 }} />
                                Déjà ajouté
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Bouton de suppression */}
                        {isAlreadySelected && hoveredMenuItem === widget.id && (
                          <Zoom in={true}>
                            <IconButton
                              onClick={(e) => { e.stopPropagation(); handleDeleteWidget(widget.id); }}
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 10,
                                backgroundColor: "#ff4757",
                                color: "#fff",
                                width: 26,
                                height: 26,
                                boxShadow: "0 4px 12px rgba(255, 71, 87, 0.3)",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: "#ff3838",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Zoom>
                        )}
                      </Paper>
                    </Zoom>
                  );
                })}
              </Box>
            </Collapse>

            {/* État réduit - icônes uniquement */}
            <Collapse in={!menuOpen}>
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: 8,
                gap: 2,
              }}>
                {availableWidgets.slice(0, 6).map((widget) => {
                  const isSelected = selectedWidgets.some((w) => w.id === widget.id);
                  return (
                    <Tooltip key={widget.id} title={widget.name} placement="left">
                      <Box
                        onClick={() => !isSelected && onSelectWidget(widget)}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          background: isSelected
                            ? "linear-gradient(135deg, #6060B8 0%, #8282CA 100%)"
                            : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: isSelected ? "default" : "pointer",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          border: isSelected ? "none" : "2px solid transparent",
                          transition: "all 0.2s ease",
                          "&:hover": !isSelected ? {
                            borderColor: "#6060B8",
                            transform: "scale(1.05)",
                          } : {},
                        }}
                      >
                        <DashboardIcon
                          sx={{
                            fontSize: 22,
                            color: isSelected ? "#fff" : "#6060B8"
                          }}
                        />
                        {isSelected && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -2,
                              right: -2,
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              backgroundColor: "#48bb78",
                              border: "2px solid #fff",
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        </Fade>,
    document.body
  );

  return (
    <>
      {sidebarPortal}
      <Container
        maxWidth={false}
        sx={{
          padding: 0,
          margin: 0,
          minHeight: "100vh",
          width: "100%",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
        }}
      >
        <Box sx={{ padding: 4, position: "relative" }}>
          <GridLayout
            className="layout"
            layout={widgetsLayout}
            onLayoutChange={handleLayoutChange}
            cols={14.6}
            rowHeight={ROW_HEIGHT_PX}
            width={1550}
            compactType="vertical"
            preventCollision={false}
            isDraggable={isEditMode}
            isResizable={false}
            draggableHandle=".drag-handle"
            style={{ background: "transparent" }}
          >
            {gridItems}
          </GridLayout>
        </Box>
      </Container>
    </>
  );
};

export default DashboardAdmin;
